import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router";
import { Home, Search, Map, User, QrCode } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "./ui/utils";
import { maxPlatform } from "../services/maxPlatform";
import { useAppStore } from "../store/useAppStore";
import { eventsService } from "../services/eventsService";
import { applyEventPrimaryCssVar, normalizeEventHex } from "../utils/eventTheme";

const DEFAULT_PRIMARY = "#4f46e5";

const NAV_KEYS = [
  { path: "/home", labelKey: "nav.home", icon: Home },
  { path: "/search", labelKey: "nav.search", icon: Search },
  { path: "/scan", labelKey: "nav.scan", icon: QrCode, isCentral: true },
  { path: "/map", labelKey: "nav.map", icon: Map },
  { path: "/my", labelKey: "nav.profile", icon: User },
] as const;

export function BottomNav({
  stubMode = false,
  hidden = false,
}: {
  stubMode?: boolean;
  /** Hide without unmounting — avoids re-animation on every route change. */
  hidden?: boolean;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { selectedEventId } = useAppStore();
  const [primary, setPrimary] = useState(DEFAULT_PRIMARY);

  const items = stubMode
    ? NAV_KEYS.filter((item) => item.path === "/home" || item.path === "/my")
    : NAV_KEYS;

  useEffect(() => {
    let cancelled = false;
    if (!selectedEventId) {
      setPrimary(DEFAULT_PRIMARY);
      applyEventPrimaryCssVar(DEFAULT_PRIMARY);
      return;
    }
    (async () => {
      try {
        let event = await eventsService.getById(selectedEventId);
        if (!event?.theme?.primaryColor) {
          eventsService.clearCache();
          event = await eventsService.getById(selectedEventId);
        }
        if (!cancelled) {
          const next = normalizeEventHex(event?.theme?.primaryColor);
          setPrimary(next);
          applyEventPrimaryCssVar(next);
        }
      } catch {
        if (!cancelled) {
          setPrimary(DEFAULT_PRIMARY);
          applyEventPrimaryCssVar(DEFAULT_PRIMARY);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedEventId]);

  const handleNav = (path: string) => {
    maxPlatform.hapticFeedback("medium");
    navigate(path);
  };

  return (
    <nav
      aria-hidden={hidden}
      className={cn(
        "max-keyboard-hide fixed inset-x-0 z-50 flex justify-center pointer-events-none pb-[max(0.75rem,env(safe-area-inset-bottom,0px),var(--sab-fallback,0px))] transition-[opacity,transform] duration-200",
        hidden && "opacity-0 translate-y-3",
      )}
      style={{
        ["--nav-primary" as string]: primary,
        bottom: "var(--keyboard-inset, 0px)",
      }}
    >
      <div
        className={cn(
          "pointer-events-auto flex w-fit items-center gap-0.5",
          "rounded-full border border-white/[0.12] bg-liquid-glass glass-specular glass-noise px-1.5 py-1.5",
          "shadow-[0_8px_32px_rgba(15,23,42,0.14),0_2px_8px_rgba(15,23,42,0.06)]",
          hidden && "pointer-events-none",
        )}
      >
        {items.map((item) => {
          const label = t(item.labelKey);
          const isActive =
            location.pathname === item.path ||
            (item.path !== "/home" && location.pathname.startsWith(item.path));
          const Icon = item.icon;
          const isCentral = "isCentral" in item && item.isCentral;

          if (isCentral) {
            return (
              <button
                key={item.path}
                type="button"
                aria-label={label}
                tabIndex={hidden ? -1 : 0}
                onClick={() => handleNav(item.path)}
                className="relative mx-0.5 flex h-11 w-11 shrink-0 items-center justify-center active:scale-95 transition-transform"
              >
                <div
                  className="absolute inset-0 rounded-full blur-md opacity-35"
                  style={{ backgroundColor: primary }}
                />
                <div
                  className="relative flex h-11 w-11 items-center justify-center rounded-full shadow-lg"
                  style={{
                    backgroundColor: primary,
                    boxShadow: `0 8px 20px ${primary}40`,
                  }}
                >
                  <Icon size={22} className="text-white" strokeWidth={2.5} />
                </div>
              </button>
            );
          }

          return (
            <button
              key={item.path}
              type="button"
              aria-label={label}
              tabIndex={hidden ? -1 : 0}
              onClick={() => handleNav(item.path)}
              className={cn(
                "relative flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-all duration-300",
                isActive ? "" : "text-gray-400 active:text-gray-600 active:bg-gray-100/80",
              )}
              style={
                isActive
                  ? { color: primary, backgroundColor: `${primary}14` }
                  : undefined
              }
            >
              <Icon
                size={22}
                strokeWidth={isActive ? 2.5 : 2}
                className="transition-all duration-300"
              />
            </button>
          );
        })}
      </div>
    </nav>
  );
}
